import React, { useState, useCallback, useReducer, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GenericFilterParamComponent from './GenericFilterParamComponent';
import { GENERIC_FILTER_PARAM_QUERY, GENERIC_FILTER_PARAM_CONTEXTS_QUERY, GENERIC_FILTER_PARAM_TYPES_QUERY } from './queries';
import { UPDATE_GENERIC_FILTER_PARAM } from './mutations';
import { GENERIC_FILTER_PARAM_UPDATED } from './subscriptions';
import PropTypes from 'prop-types';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { initialState, reducer } from 'features/genericFilters/reducers/containerReducer';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { Card } from '@totalsoft/rocket-ui';
import { Prompt } from 'react-router-dom';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { genericFilterParamValidator } from './validators';
import { useToast } from 'hooks/toasts/useToast';
import IconButton from 'components/common/buttons/IconButton';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_GENERIC_FILTER_PARAMS_WRITE } from 'constants/rightsCodes';
import ViewListIcon from '@mui/icons-material/ViewList';
import WrappedFakeText from 'components/common/WrappedFakeText';

const GenericFilterParamContainer = ({ paramId, onChangeGenericParamTitle }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [validation, validate] = useDirtyFieldValidation(genericFilterParamValidator);
  const [localState, dispatch] = useReducer(reducer, initialState);
  const [saving, setSaving] = useState(false);

  const canWrite = usePermission(ADMIN_GENERIC_FILTER_PARAMS_WRITE);

  const { data, loading, refetch } = useQuery(GENERIC_FILTER_PARAM_QUERY, {
    variables: {
      paramId
    },
    skip: !paramId
  });

  useEffect(() => {
    if (data)
      dispatch({
        type: 'OnInitState',
        genericFilterParam: data.genericFilterParam,
        genericFilterParamXContexts: data.genericFilterParam.genericFilterParamXContexts
      });
  }, [data]);

  useEffect(() => {
    onChangeGenericParamTitle(localState.genericFilterParam.paramName);
  }, [localState.genericFilterParam.paramName, onChangeGenericParamTitle]);

  useEffect(() => {
    validate(localState.genericFilterParam);
  }, [localState.genericFilterParam, validate]);

  const { data: contextsData } = useQuery(GENERIC_FILTER_PARAM_CONTEXTS_QUERY);

  const { data: typesData } = useQuery(GENERIC_FILTER_PARAM_TYPES_QUERY);

  const [updateGenericFilterParam] = useMutation(UPDATE_GENERIC_FILTER_PARAM, {
    onError: () => {
      setSaving(false);
    }
  });

  useSubscription(GENERIC_FILTER_PARAM_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('GenericFilterParam.GenericFilterParamUpdated'), 'success');
      const genericFilterParamId = data.genericFilterParamUpdated.genericFilterParam.paramId;
      dispatch({ type: 'OnHeaderSaved', genericFilterParamId });
      client.writeQuery({
        query: GENERIC_FILTER_PARAM_QUERY,
        data: {
          genericFilterParam: {
            __typename: 'GenericFilterParam',
            ...data.genericFilterParamUpdated.genericFilterParam,
            genericFilterParamXContexts: data.genericFilterParamUpdated.genericFilterParam.genericFilterParamXContexts
          }
        },
        variables: {
          paramId
        }
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterParams' });
      client.cache.gc();
      setSaving(false);
    }
  });

  const handlePropertyChanged = useCallback(
    (propertyName, value) => {
      dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value, contexts: contextsData?.genericFilterParamContexts });
    },
    [contextsData]
  );

  const handleSave = () => {
    // eslint-disable-next-line no-unused-vars
    const { changed, genericFilterParamXContextsIds, ...genericFilter } = localState.genericFilterParam;
    const input = { ...genericFilter };
    if (!validate(input)) return;
    setSaving(true);
    return updateGenericFilterParam({ variables: { input } });
  };

  const onLanguageChange = useCallback(() => {
    refetch();
  }, [refetch]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  const disabled = useMemo(() => !canWrite || saving, [canWrite, saving]);

  return (
    <>
      <Prompt when={localState.genericFilterParam.changed} message={t('GenericFilterParam.LeavingWithoutSavingGenericFilter')} />
      <Card
        icon={ViewListIcon}
        title={t('GenericFilterParam.GeneralData')}
        actions={<IconButton type='save' onClick={handleSave} disabled={!localState.genericFilterParam.changed || disabled} />}
        iconColor='primary'
      >
        {loading || localState === initialState ? (
          <WrappedFakeText lines={6} />
        ) : (
          <GenericFilterParamComponent
            genericFilterParam={localState.genericFilterParam}
            genericFilterParamContexts={contextsData ? contextsData.genericFilterParamContexts : []}
            genericFilterTypes={typesData ? typesData.genericFilterParamTypes : []}
            onPropertyChanged={handlePropertyChanged}
            validation={validation}
            disabled={disabled}
          />
        )}
      </Card>
    </>
  );
};

GenericFilterParamContainer.propTypes = {
  paramId: PropTypes.number,
  onChangeGenericParamTitle: PropTypes.func
};

export default GenericFilterParamContainer;
